<!-- search-filters.component.html -->
<sxw-side-dialog
  [visible]="visible()"
  dialogClasses="filters-dialog"
  (onHideDialog)="visible.set(false)"
  appendTo="body"
  outletName="popups"
  [closable]="true"
  [dialogStyles]="{ width: '500px' }">
  <ng-template #header>
    <h2>{{ "filters.title" | translate }}</h2>
  </ng-template>

  <ng-template #content>
    <sxw-form [formGroup]="formModel.formGroup()" class="space-y-4">
      <div class="filters-container">
        <!-- Match Preferences -->
        <div class="filters-section">
          <div class="filters-section-title">
            <sxw-svg-icon width="24" height="24" name="heart" class="lucide lucide-house w-6 h-6 mr-3 text-black"></sxw-svg-icon>
            <div>
              <h3 class="text-xl font-semibold text-gray-800">{{ "filters.match.title" | translate }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ "filters.match.description" | translate }}</p>
            </div>
          </div>

          <div>
            <div class="filter-options-container flex flex-col gap-3">
              <app-property-match-options-input formControlName="match"></app-property-match-options-input>
            </div>
          </div>
        </div>

        <!-- Location Preferences -->
        <div class="filters-section">
          <div class="filters-section-title">
            <sxw-svg-icon name="address" class="w-6 h-6 mr-3 text-black" width="24" height="24"></sxw-svg-icon>
            <div>
              <h3 class="text-xl font-semibold text-gray-800">{{ "filters.city.title" | translate }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ "filters.city.description" | translate }}</p>
            </div>
          </div>

          <div class="space-y-6">
            <div class="flex flex-col gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1.5">
                  {{ "filters.city.label" | translate }}
                </label>
                <div class="flex tw-gap-2">
                  <sxw-addresses-autocomplete
                    #addressElement
                    theme="address-input-theme"
                    class="w-full"
                    placeholder="filters.city.placeholder"
                    (onAddressChanged)="addCity($event)"
                    [types]="['(cities)']"
                    [country]="{ short: 'NL' }">
                  </sxw-addresses-autocomplete>
                  <sxw-button label="add" [outlined]="true" size="small"></sxw-button>
                </div>
                <div class="flex flex-wrap tw-gap-2">
                  @for (city of formModel.formGroup().value.city; track city; let i = $index) {
                    <div class="city-badge mt-4">
                      <span class="city-label">{{ city }}</span>
                      <button type="button" class="city-remove" (click)="removeCity(i)">✕</button>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Budget Range -->
        <div class="filters-section">
          <div class="filters-section-title">
            <sxw-svg-icon width="24" height="24" name="euro" class="lucide lucide-house w-6 h-6 mr-3 text-black"></sxw-svg-icon>
            <div>
              <h3 class="text-xl font-semibold text-gray-800">{{ "filters.budget.title" | translate }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ "filters.budget.description" | translate }}</p>
            </div>
          </div>
          <div class="space-y-6">
            <div>
              <div class="flex justify-between items-center">
                <label class="block text-sm font-medium text-gray-700 mb-1.5">{{ "filters.budget.label" | translate }}</label>
              </div>
              <div class="flex gap-4 mt-2">
                <div class="w-1/2">
                  <label class="block text-xs text-gray-500 mb-1">{{ "filters.budget.min" | translate }}</label>
                  <sxw-number-input formControlName="minBudget" [minValue]="0" [stepValue]="100"></sxw-number-input>
                </div>
                <div class="w-1/2">
                  <label class="block text-xs text-gray-500 mb-1">{{ "filters.budget.max" | translate }}</label>
                  <sxw-number-input formControlName="maxBudget" [minValue]="0" [stepValue]="100"></sxw-number-input>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Preferred Home Types -->
        <div class="filters-section">
          <div class="filters-section-title">
            <sxw-svg-icon width="24" height="24" name="homeType" class="lucide lucide-house w-6 h-6 mr-3 text-black"></sxw-svg-icon>
            <div>
              <h3 class="text-xl font-semibold text-gray-800">{{ "filters.propertyType.title" | translate }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ "filters.propertyType.description" | translate }}</p>
            </div>
          </div>
          <div class="space-y-6">
            <sxw-checkbox-list
              formControlName="propertyType"
              [items]="homeTypes"
              dataKey="id"
              labelKey="label"
              valueKey="id"
              [size]="'small'"
              [theme]="'grid grid-cols-1 sm:grid-cols-2 gap-3'"
              [checkboxTheme]="'feature-card flex items-center gap-3 border border-gray-200 rounded-xl p-4 w-full cursor-pointer'">
              <ng-template #checkboxItemTemplate let-item let-checked="checked">
                <span class="card-content flex items-center gap-3 flex-1">
                  <span class="text-2xl mr-3">{{ item.emoji }}</span>
                  <span class="text-sm font-medium text-gray-800">{{ item.label | translate }}</span>
                </span>
              </ng-template>
            </sxw-checkbox-list>
          </div>
        </div>

        <!-- Size & Space Preferences -->
        <div class="filters-section">
          <div class="filters-section-title">
            <sxw-svg-icon name="doubleBed" class="w-6 h-6 mr-3 text-black" width="23" height="23"></sxw-svg-icon>
            <h3 class="text-xl font-semibold text-gray-800">{{ "filters.space.title" | translate }}</h3>
          </div>

          <div>
            <div class="flex justify-between items-center">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">{{ "filters.rooms.title" | translate }}</label>
              <span class="text-sm font-semibold text-black">
                {{ formModel.formGroup().value.rooms?.min ?? 1 }} - {{ formModel.formGroup().value.rooms?.max ?? 10 }}
              </span>
            </div>
            <app-range-input
              [minValue]="1"
              [maxValue]="10"
              [ignoreDefaults]="true"
              minLabel="filters.rooms.min"
              maxLabel="filters.rooms.max"
              formControlName="rooms"></app-range-input>
          </div>

          <div class="mt-20px">
            <div class="flex justify-between items-center">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">{{ "filters.livingSpace.title" | translate }}</label>
              <span class="text-sm font-semibold text-black">
                {{ formModel.formGroup().value.livingSpace?.min ?? 1 }} m² - {{ formModel.formGroup().value.livingSpace?.max ?? 500 }} m²
              </span>
            </div>
            <app-range-input
              [minValue]="1"
              [maxValue]="500"
              [step]="10"
              [ignoreDefaults]="true"
              minLabel="filters.livingSpace.min"
              maxLabel="filters.livingSpace.max"
              formControlName="livingSpace"></app-range-input>
          </div>
        </div>

        <!-- Must-Have Features -->
        <div class="filters-section">
          <div class="filters-section-title">
            <sxw-svg-icon width="24" height="24" name="heart" class="lucide lucide-house w-6 h-6 mr-3 text-black"></sxw-svg-icon>
            <div>
              <h3 class="text-xl font-semibold text-gray-800">{{ "filters.features.title" | translate }}</h3>
              <p class="text-sm text-gray-500 mt-1">{{ "filters.features.description" | translate }}</p>
            </div>
          </div>
          <div class="space-y-6">
            <sxw-checkbox-list
              formControlName="features"
              [items]="homeFeatures"
              dataKey="id"
              labelKey="label"
              valueKey="id"
              [size]="'small'"
              [theme]="'grid grid-cols-1 sm:grid-cols-2 gap-3 p-relative'"
              [checkboxTheme]="'feature-card flex items-center border border-gray-200 rounded-xl p-3 w-full cursor-pointer'">
              <ng-template #checkboxItemTemplate let-item let-checked="checked">
                <span class="card-content flex items-center flex-1">
                  <span class="text-lg mr-2">{{ item.emoji }}</span>
                  <span class="text-sm font-medium text-gray-700">{{ item.label | translate }}</span>
                </span>
              </ng-template>
            </sxw-checkbox-list>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="filters-actions">
          <sxw-button label="filters.actions.clear" (click)="clearFilters()" severity="secondary"></sxw-button>
          <sxw-button label="filters.actions.apply" (click)="submitFilters()"></sxw-button>
        </div>
      </div>
    </sxw-form>
  </ng-template>
</sxw-side-dialog>
